import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../theme';
import mark from '../solutions/onehot/brand/onehot-q.png';

// Logo animation constants (pixel-perfect measurements)
const FULL_W_PX = 1024;
const FULL_H_PX = 297;
const FULL_ASPECT = FULL_W_PX / FULL_H_PX;
const Q_ICON_HEIGHT_RATIO = 0.682237;
const Q_ICON_LEFT_RATIO = 0.153077;
const Q_ICON_TOP_RATIO = 0.254202;

// Animated Logo Component (14px - Medium-Small)
// Accepts hovered prop from parent to allow pill-level hover control
function AnimatedLogo({ hovered, size = 14 }: { hovered: boolean; size?: number }) {
  const qSize = size;
  const qIconH = qSize;
  const fullLogoH = Math.round(qIconH / Q_ICON_HEIGHT_RATIO);
  const fullLogoW = Math.round(fullLogoH * FULL_ASPECT);
  const qIconLeftInFull = Math.round(fullLogoW * Q_ICON_LEFT_RATIO);
  const qIconTopInFull = Math.round(fullLogoH * Q_ICON_TOP_RATIO);
  
  const expandedPadding = Math.round(Math.max(4, qSize * 0.35));
  
  const collapsedW = qIconH;
  const collapsedH = qIconH;
  const expandedW = fullLogoW + (expandedPadding * 2);
  const expandedH = fullLogoH;
  
  const qExpandedLeft = qIconLeftInFull + expandedPadding;
  const qExpandedTop = qIconTopInFull;
  
  const easeOut = 'cubic-bezier(0.16, 1, 0.3, 1)';

  return (
    <div
      className="relative select-none"
      style={{
        width: hovered ? expandedW : collapsedW,
        height: hovered ? expandedH : collapsedH,
        overflow: 'hidden',
        transition: `width 0.45s ${easeOut}, height 0.45s ${easeOut}`,
        willChange: 'width, height',
      }}
    >
      <img
        src="/logo-sqwish-full-transparent.png"
        alt="Sqwish"
        draggable={false}
        style={{
          position: 'absolute',
          top: 0,
          left: expandedPadding,
          height: fullLogoH,
          width: fullLogoW,
          opacity: hovered ? 1 : 0,
          transform: hovered
            ? 'translate3d(0, 0, 0) scale(1)'
            : `translate3d(${-qIconLeftInFull - expandedPadding}px, ${-qIconTopInFull}px, 0) scale(0.92)`,
          transformOrigin: `${qIconLeftInFull + expandedPadding}px ${qIconTopInFull}px`,
          transition: `transform 0.45s ${easeOut}, opacity 0.35s ${easeOut}`,
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
        }}
      />
      <img
        src="/logo-q-full.png"
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          top: hovered ? qExpandedTop : 0,
          left: hovered ? qExpandedLeft : 0,
          height: qIconH,
          width: qIconH,
          objectFit: 'contain',
          opacity: hovered ? 0 : 1,
          transition: `top 0.45s ${easeOut}, left 0.45s ${easeOut}, opacity 0.3s ${easeOut}`,
          pointerEvents: 'none',
          willChange: 'top, left, opacity',
          backfaceVisibility: 'hidden',
        }}
      />
    </div>
  );
}

// Logo Pill - wraps AnimatedLogo with hover state at the pill level
function LogoPill() {
  const [hovered, setHovered] = useState(false);
  
  return (
    <SimplePill>
      <a
        href="/"
        className="flex items-center px-[15px] rounded-[120px] h-[30px] backdrop-blur-md bg-foreground/[0.06] hover:bg-foreground/[0.12] active:bg-foreground/[0.12] text-foreground/70 hover:text-foreground active:text-foreground font-light py-[5px] transition-all duration-200 dark:bg-white/8 dark:hover:bg-white/15 dark:active:bg-white/15 dark:text-white/85 dark:hover:text-white dark:active:text-white tap-highlight-none"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <AnimatedLogo hovered={hovered} />
      </a>
    </SimplePill>
  );
}

function SimplePill({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-[120px] border border-foreground/12 dark:border-white/[0.06] backdrop-blur-sm bg-foreground/[0.04] dark:bg-white/4 p-1 ${className}`}>
      <div className="flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}

const PILL_ITEM =
  'px-[15px] py-[10px] rounded-[120px] h-[30px] backdrop-blur-md flex items-center justify-center transition-all duration-200 text-[11px] uppercase tracking-wide whitespace-nowrap flex-shrink-0 tap-highlight-none';
const PILL_DEFAULT =
  `${PILL_ITEM} bg-foreground/[0.06] hover:bg-foreground/[0.12] active:bg-foreground/[0.12] text-foreground/70 hover:text-foreground active:text-foreground ` +
  'dark:bg-white/8 dark:hover:bg-white/15 dark:active:bg-white/15 dark:text-white/85 dark:hover:text-white dark:active:text-white';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PILL_ACTIVE =
  `${PILL_ITEM} bg-foreground/[0.12] text-foreground font-normal ` +
  'dark:bg-white/15 dark:text-white';

function AdaptionDropdown({
  label,
  items,
  fontClass,
  isActive,
}: {
  label: string;
  items: { label: string; href: string }[];
  fontClass?: string;
  isActive?: boolean;
}) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const open = locked || hoverOpen;

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom,
        left: rect.left,
      });
    }
  };

  const enter = () => {
    clearTimeout(timeout.current);
    updateDropdownPosition();
    setHoverOpen(true);
  };

  const leave = () => {
    timeout.current = setTimeout(() => setHoverOpen(false), 150);
  };

  const handleClick = () => {
    // Scroll the button into view within its scrollable container
    if (buttonRef.current) {
      buttonRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    
    // Small delay to let scroll start, then toggle state
    setTimeout(() => {
      updateDropdownPosition();
      setLocked(prev => {
        if (prev) {
          setHoverOpen(false);
        }
        return !prev;
      });
    }, 50);
  };

  // Update dropdown position during scroll
  useEffect(() => {
    if (!open) return;
    
    const handleScroll = () => {
      updateDropdownPosition();
    };
    
    // Listen for scroll on any parent
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const clickedInContainer = containerRef.current?.contains(target);
      const clickedInDropdown = dropdownRef.current?.contains(target);
      
      if (locked && !clickedInContainer && !clickedInDropdown) {
        setLocked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      clearTimeout(timeout.current);
    };
  }, [locked]);

  const solidBgLight = 'bg-[hsl(30,2%,81%)]';
  const solidBgDark = 'dark:bg-[hsl(210,3%,26%)]';

  const dropdownMenu = (
    <div
      ref={dropdownRef}
      className={[
        `fixed w-[118px] ${solidBgLight} ${solidBgDark} rounded-b-[15px] overflow-hidden z-[200]`,
        open ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ].join(' ')}
      style={{ 
        top: dropdownPos.top,
        left: dropdownPos.left,
        maxHeight: open ? `${items.length * 32 + 8}px` : '0px',
        transition: 'opacity 200ms ease-out, max-height 200ms ease-out',
      }}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      <div className="px-1 pb-1.5 pt-0.5 flex flex-col">
        {items.map(item => (
          <a
            key={item.href}
            href={item.href}
            className={`block w-full text-center font-light text-[11px] uppercase tracking-wide text-foreground/60 hover:text-foreground hover:bg-foreground/[0.08] dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10 rounded-lg py-1.5 px-2 transition-all duration-150 tap-highlight-none ${fontClass || ''}`}
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );

  // Determine button style based on open and active states
  const getButtonStyle = () => {
    if (open) {
      return `rounded-t-[15px] rounded-b-none ${solidBgLight} ${solidBgDark} text-foreground/80 dark:text-white/90`;
    }
    if (isActive) {
      return 'rounded-[120px] bg-foreground/[0.12] text-foreground font-normal dark:bg-white/15 dark:text-white backdrop-blur-md';
    }
    return 'rounded-[120px] bg-foreground/[0.06] hover:bg-foreground/[0.12] active:bg-foreground/[0.12] text-foreground/70 hover:text-foreground active:text-foreground dark:bg-white/8 dark:hover:bg-white/15 dark:active:bg-white/15 dark:text-white/85 dark:hover:text-white dark:active:text-white backdrop-blur-md';
  };

  return (
    <>
      <div
        ref={containerRef}
        className="relative z-[100]"
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        <button
          ref={buttonRef}
          onClick={handleClick}
          data-active={isActive ? 'true' : undefined}
          className={[
            'relative flex items-center justify-center gap-2 w-[118px] px-[15px] h-[30px]',
            'transition-all duration-200 cursor-pointer uppercase text-[11px] tracking-wide tap-highlight-none',
            getButtonStyle(),
            fontClass || '',
          ].join(' ')}
        >
          <span className={isActive && !open ? 'font-normal' : 'font-light'}>{label}</span>
          <span className={`text-[8px] leading-none transition-transform duration-200 opacity-70 ${open ? 'rotate-180' : ''}`}>▼</span>
        </button>
      </div>
      {createPortal(dropdownMenu, document.body)}
    </>
  );
}

// Nav link that scrolls into view on click (for mobile) and shows active state
function ScrollableNavLink({ 
  href, 
  children, 
  className,
  isActive,
}: { 
  href: string; 
  children: React.ReactNode; 
  className: string;
  isActive?: boolean;
}) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  return (
    <a 
      href={href} 
      className={isActive ? className.replace(PILL_DEFAULT, PILL_ACTIVE) : className} 
      onClick={handleClick}
      data-active={isActive ? 'true' : undefined}
    >
      {children}
    </a>
  );
}

function ThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="hidden min-[400px]:block fixed top-7 min-[650px]:top-9 right-4 min-[650px]:right-6 z-[100] p-1.5 transition-all duration-200 hover:scale-110 cursor-pointer"
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeOpacity="0.7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="hover:stroke-white transition-all duration-200">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="black" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="hover:stroke-black hover:stroke-opacity-80 transition-all duration-200">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

// Helper to check if a path matches current location
function useCurrentPath() {
  const [path] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  return path;
}

// Check if dropdown contains active route
function isDropdownActive(items: { href: string }[], currentPath: string) {
  return items.some(item => currentPath === item.href || currentPath.startsWith(item.href + '/'));
}

export function Header() {
  const fontClass = "font-[ui-sans-serif,system-ui,sans-serif]";
  const currentPath = useCurrentPath();

  const researchItems = [
    { label: 'Manifesto', href: '/manifesto' },
  ];
  const solutionsItems = [
    { label: 'OneHot', href: '/onehot' },
  ];
  
  const isHomeActive = currentPath === '/';
  const isBlogActive = currentPath === '/blog' || currentPath.startsWith('/blog/');
  const isResearchActive = isDropdownActive(researchItems, currentPath);
  const isSolutionsActive = isDropdownActive(solutionsItems, currentPath);

  // Track if scroll container has been positioned
  const [isPositioned, setIsPositioned] = useState(isHomeActive);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasPositionedRef = useRef(false);

  // Set scroll position synchronously before paint to avoid flicker
  useLayoutEffect(() => {
    if (hasPositionedRef.current || isHomeActive) return;
    
    const el = scrollContainerRef.current;
    if (el) {
      const activeEl = el.querySelector('[data-active="true"]') as HTMLElement;
      if (activeEl) {
        const containerRect = el.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        const scrollLeft = activeRect.left - containerRect.left + el.scrollLeft
          - (containerRect.width / 2) + (activeRect.width / 2);
        el.scrollLeft = scrollLeft;
      }
      hasPositionedRef.current = true;
      setIsPositioned(true);
    }
  }, [isHomeActive, isResearchActive, isSolutionsActive, isBlogActive]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[100] ${fontClass}`}>
      {/* Mobile: centered nav with logo to its left (below 650px) */}
      <div className="min-[650px]:hidden">
        <div className="flex items-center justify-center gap-2 px-3 py-3 w-full">
          {/* Logo - positioned to the left of centered nav */}
          <div className="shrink-0">
            <LogoPill />
          </div>
          
          {/* Scrollable nav pill - centered, rounded container with internal scroll */}
          <div 
            className="min-w-0 flex-1 max-w-[200px] rounded-[120px] border border-foreground/12 dark:border-white/[0.06] backdrop-blur-sm bg-foreground/[0.04] dark:bg-white/4 overflow-hidden"
            style={{ visibility: isPositioned ? 'visible' : 'hidden' }}
          >
            <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide px-1 py-1">
              <div className="flex items-center gap-2 w-max">
                <ScrollableNavLink href="/" className={PILL_DEFAULT} isActive={isHomeActive}>Home</ScrollableNavLink>
                <AdaptionDropdown
                  label="Research"
                  fontClass={fontClass}
                  items={researchItems}
                  isActive={isResearchActive}
                />
                <AdaptionDropdown
                  label="Solutions"
                  fontClass={fontClass}
                  items={solutionsItems}
                  isActive={isSolutionsActive}
                />
                <a href="https://www.notion.so/sqwish/Careers-page-2329b33f1c35812e842cdd8d927b295b" target="_blank" rel="noopener noreferrer" className={PILL_DEFAULT}>Careers</a>
                <ScrollableNavLink href="/blog" className={PILL_DEFAULT} isActive={isBlogActive}>Blog</ScrollableNavLink>
                <a href="https://docs.sqwish.ai" target="_blank" rel="noopener noreferrer" className={PILL_DEFAULT}>Docs</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: centered floating nav (650px and above) */}
      <div className="hidden min-[650px]:flex justify-center pt-8">
        <div className="flex items-center gap-3">
          <LogoPill />

          <SimplePill>
            <div className="flex w-full rounded-[15px] gap-[10px]">
              <a href="/" className={isHomeActive ? PILL_ACTIVE : PILL_DEFAULT}>Home</a>
              <AdaptionDropdown
                label="Research"
                fontClass={fontClass}
                items={researchItems}
                isActive={isResearchActive}
              />
              <AdaptionDropdown
                label="Solutions"
                fontClass={fontClass}
                items={solutionsItems}
                isActive={isSolutionsActive}
              />
              <a href="https://www.notion.so/sqwish/Careers-page-2329b33f1c35812e842cdd8d927b295b" target="_blank" rel="noopener noreferrer" className={PILL_DEFAULT}>Careers</a>
              <a href="/blog" className={isBlogActive ? PILL_ACTIVE : PILL_DEFAULT}>Blog</a>
              {/* Below 760px the row would reach the theme toggle; the footer links the docs there. */}
              <a href="https://docs.sqwish.ai" target="_blank" rel="noopener noreferrer" className={`${PILL_DEFAULT} max-[759px]:hidden`}>Docs</a>
            </div>
          </SimplePill>

          {/* OneHot as the console sets it: the sticker beside the name in Chesna Grotesk
              (declared in onehot.css), the name's box cut to its capitals so the two centre
              together. The sticker stands about 13px tall. The pill takes the opposite of the
              page's colour, and on the dark pill the sticker's outline sinks in, so it takes the
              console's larger box for a dark ground. */}
          <SimplePill className="hidden lg:block">
            <a
              href="https://console.sqwish.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="h-[30px] pl-[12px] pr-[15px] rounded-[120px] flex items-center gap-[4.67px] whitespace-nowrap flex-shrink-0 tap-highlight-none transition-all duration-200 backdrop-blur-md bg-foreground/80 text-background hover:bg-foreground active:bg-foreground dark:bg-white/80 dark:text-[hsl(220,25%,6%)] dark:hover:bg-white dark:active:bg-white"
            >
              <img src={mark} alt="" width={256} height={252} className="w-[17.4px] h-[17.4px] dark:w-[16.2px] dark:h-[16.2px] object-contain" />
              <span className="-mt-[0.0998em] -mb-[0.2em] -ml-[0.04em] font-['Chesna_Grotesk','Source_Sans_3',sans-serif] text-[14px] font-semibold leading-none tracking-[-0.02em]">OneHot</span>
            </a>
          </SimplePill>
        </div>
      </div>

      <ThemeToggle />
    </nav>
  );
}

export default Header;
